import { Flex, Typography, Row, Col } from 'antd';
import { EyeOutlined, LoadingOutlined } from '@ant-design/icons';

import { SidebarDivider } from 'layouts/DashboardLayout/Sidebar';

import ClientMetaSkeleton from 'components/ClientMetaSkeleton/ClientMetaSkeleton';
import DisplayValue from 'components/DisplayValue/DisplayValue';
import AlertTooltip from 'components/AlertTooltip/AlertTooltip';
import LocationAndTime from 'components/LocationAndTime/LocationAndTime';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import useAgent from 'hooks/useAgent';
import useApplicantFullSsn from 'hooks/useApplicantFullSsn';
import useCustomerInfo from 'hooks/useCustomerInfo';

import { SIZE } from 'utilities/constants';
import { formatDate, getAgeFromDate } from 'utilities/datesAndTimes';

const ApplicantMeta = () => {
  const { isSuperUser } = useAgent();

  const { customerInfo, customerInfoQuery } = useCustomerInfo();
  const [fullSsn, getFullSsn, ssnFailureMessage, { loading: loadingFullSsn }] =
    useApplicantFullSsn();

  const getApplicantName = () => {
    const { firstName, middleName, lastName } = customerInfo;
    return `${firstName} ${(middleName || '').charAt(0)} ${lastName}`;
  };

  return (
    <>
      <QueryBoundary
        messageHeight="372px"
        errorMessage="Failed to load Applicant Information."
        query={customerInfoQuery}
        skeleton={ClientMetaSkeleton}
      >
        <Flex vertical={true}>
          <Typography.Title level={2} style={{ marginBottom: 0 }}>
            <span className="fs-mask">{getApplicantName()}</span>
          </Typography.Title>

          <SidebarDivider />

          <LocationAndTime client={customerInfo}></LocationAndTime>

          <SidebarDivider />

          <Row gutter={[10, '1rem']}>
            <Col sm={11}>
              <AlertTooltip
                type="error"
                placement="topLeft"
                open={!!ssnFailureMessage}
                title={ssnFailureMessage}
                styles={{
                  root: {
                    position: 'fixed',
                  },
                }}
              >
                <DisplayValue
                  label="SSN"
                  labelIcon={
                    isSuperUser
                      ? loadingFullSsn
                        ? LoadingOutlined
                        : EyeOutlined
                      : undefined
                  }
                  onLabelIconClick={
                    loadingFullSsn.loading ? undefined : getFullSsn
                  }
                  size={SIZE.MD}
                >
                  <span className="fs-mask">
                    {fullSsn ? fullSsn : `_____ ${customerInfo.lastFourSSN}`}
                  </span>
                </DisplayValue>
              </AlertTooltip>
            </Col>
            <Col sm={13}>
              <DisplayValue
                label="Primary Phone"
                size={SIZE.MD}
                canCopy={true}
                copiedValue={`${customerInfo.phoneNumber}`}
              >
                <span className="fs-mask">{customerInfo.phoneNumber}</span>
              </DisplayValue>
            </Col>
            <Col sm={11}>
              <DisplayValue
                label="Date of Birth"
                size={SIZE.MD}
                canCopy={true}
                copiedValue={formatDate(customerInfo.dateOfBirth)}
              >
                {formatDate(customerInfo.dateOfBirth)} &bull;{' '}
                {getAgeFromDate(customerInfo.dateOfBirth)} yrs
              </DisplayValue>
            </Col>
            <Col sm={13}>
              <DisplayValue
                label="Email"
                canCopy={true}
                size={SIZE.MD}
                copiedValue={customerInfo.email}
              >
                <span className="fs-mask">{customerInfo.email}</span>
              </DisplayValue>
            </Col>
            <Col sm={24}>
              <DisplayValue
                label="Address"
                size={SIZE.MD}
                canCopy={true}
                copiedValue={`${customerInfo.address?.street1} ${customerInfo.address?.city}, ${customerInfo.address?.state} ${customerInfo.address?.postalCode}`}
              >
                <span className="fs-mask">
                  {`${customerInfo.address?.street1} ${customerInfo.address?.street2}`.trim()}
                  <br></br>
                  {`${customerInfo.address?.city}, ${customerInfo.address?.state} ${customerInfo.address?.postalCode}`}
                </span>
              </DisplayValue>
            </Col>
          </Row>
        </Flex>
      </QueryBoundary>
    </>
  );
};

export default ApplicantMeta;
